// FormUsers.jsx (phosphor-react + uses revised components)
import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Loading, TextInput, SelectSearch, Checkbox } from "../../components"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { motion } from "framer-motion"
import { Camera, ShieldCheck, Trash, UploadSimple, User as UserIcon } from "phosphor-react"

function FormUsers() {
  const navigate = useNavigate()
  const { id } = useParams()

  const [loading, setLoading] = useState(false)
  const [roles, setRoles] = useState([])
  const [formData, setFormData] = useState({
    doubleAuth: false,
    email: "",
    fullName: "",
    phone: "",
    role_id: "",
    etat: "active",
    password: "",
    image: null,
  })

  useEffect(() => {
    getRoles()
    if (id) getUserToUpdate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleChange = (e) => {
    if (e?.target?.type === "file") {
      const file = e.target.files[0]
      if (!file) return

      if (file.size > 15 * 1024 * 1024) {
        toast.warning("File size exceeds 15 MB limit")
        e.target.value = ""
        return
      }

      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => setFormData((s) => ({ ...s, image: reader.result }))
      return
    }

    const name = e?.target?.name
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value
    setFormData((s) => ({ ...s, [name]: value }))
  }

  const getRoles = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get("/role/search")
      setRoles(response?.data?.data || [])
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch roles")
    } finally {
      setLoading(false)
    }
  }

  const getUserToUpdate = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get(`/users/get/${id}`)
      if (response?.data?.data) setFormData((s) => ({ ...s, ...response.data.data }))
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch user")
    } finally {
      setLoading(false)
    }
  }

  const save = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      const response = id
        ? await axiosInstance.put(`/users/update/${id}`, formData)
        : await axiosInstance.post("/users/create", formData)

      toast.success(response?.data?.message || "Saved")
      navigate(-1)
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save user")
    } finally {
      setLoading(false)
    }
  }

  const roleOptions = useMemo(
    () => (roles || []).map((r) => ({ value: r.id, label: r.name })),
    [roles]
  )

  const statusOptions = useMemo(
    () => [
      { value: "active", label: "Active" },
      { value: "inactive", label: "Inactive" },
    ],
    []
  )

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col min-h-screen bg-neutral-50 dark:bg-neutral-950/40"
    >
      {loading && <Loading />}

      <div className="flex-1 py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl text-start font-semibold text-neutral-900 dark:text-white">
            {id ? "Edit User" : "New User"}
          </h1>
          <p className="mt-1 text-start text-sm text-neutral-600 dark:text-neutral-400">
            {id ? "Update user information" : "Add a new user to the system"}
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {/* Sidebar */}
          <div className="space-y-3">
            {/* Profile Photo */}
            <div className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/70 overflow-hidden">
              <div className="p-6">
                <h3 className="text-base font-semibold text-neutral-900 dark:text-white">Profile Photo</h3>

                <div className="mt-4 flex flex-col items-center">
                  <div className="relative">
                    <div className="h-32 w-32 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                      {formData.image ? (
                        <img src={formData.image} alt="Profile" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Camera size={28} className="text-neutral-400" />
                        </div>
                      )}
                    </div>

                    <label
                      htmlFor="photo-upload"
                      className="absolute bottom-0 right-0 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#2B2B35] text-white hover:bg-cyan-400"
                      title="Upload"
                    >
                      <UploadSimple size={18} />
                      <input
                        id="photo-upload"
                        type="file"
                        className="hidden"
                        onChange={handleChange}
                        accept="image/*"
                        name="image"
                      />
                    </label>
                  </div>

                  {formData.image && (
                    <button
                      type="button"
                      onClick={() => setFormData((s) => ({ ...s, image: null }))}
                      className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-rose-600 hover:text-rose-700"
                    >
                      <Trash size={16} />
                      Remove photo
                    </button>
                  )}

                  <p className="mt-2 text-xs text-neutral-600 dark:text-neutral-400">
                    JPG, GIF or PNG. Max size of 15MB.
                  </p>
                </div>
              </div>
            </div>

            {/* 2FA Settings (uses revised Checkbox) */}
            <div className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/70 overflow-hidden">
              <div className="p-6">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <ShieldCheck size={22} className="text-neutral-600 dark:text-neutral-300" />
                  </div>

                  <div className="flex-1">
                    <Checkbox
                      checked={!!formData.doubleAuth}
                      onChange={(e) => setFormData((s) => ({ ...s, doubleAuth: e.target.checked }))}
                      label="Two-Factor Authentication"
                      description="Secure the account with 2FA via email."
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Form */}
          <div className="lg:col-span-2">
            <form onSubmit={save} className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/70 overflow-hidden">
              <div className="p-6 space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <TextInput
                    label="Full Name"
                    name="fullName"
                    value={formData.fullName || ""}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    icon={UserIcon}
                  />

                  <TextInput
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email || ""}
                    onChange={handleChange}
                    placeholder="Enter email address"
                  />

                  <TextInput
                    label="Phone Number"
                    name="phone"
                    value={formData.phone || ""}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                  />

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 text-start mb-1">
                      Role
                    </label>
                    <SelectSearch
                      value={formData.role_id || ""}
                      onChange={(val) => setFormData((s) => ({ ...s, role_id: val }))}
                      options={roleOptions}
                      placeholder="Select a role"
                      searchable
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 text-start mb-1">
                      Status
                    </label>
                    <SelectSearch
                      value={formData.etat || "active"}
                      onChange={(val) => setFormData((s) => ({ ...s, etat: val }))}
                      options={statusOptions}
                      placeholder="Select status"
                      searchable={false}
                    />
                  </div>

                  {!id && (
                    <TextInput
                      label="Password"
                      name="password"
                      type="password"
                      value={formData.password || ""}
                      onChange={handleChange}
                      placeholder="Enter password"
                    />
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-neutral-200/70 dark:border-neutral-800">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] hover:bg-cyan-400 focus:outline-none"
                >
                  {id ? "Update User" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default FormUsers
